import { useState } from 'react'

import {
   API_FAILED,
   API_FETCHING,
   API_INITIAL,
   API_SUCCESS,
   APIStatus
} from '@flowwai-frontend/shared/api-common'
import { useCustomT } from '@flowwai-frontend/shared/common-components'

interface TriggerAPIArgType {
   fetchUrl: string
   method: 'GET' | 'POST'
   requestHeader?: Record<string, string>
   onSuccess: (data: unknown | null) => void
   onFailure: (errorMessage: string) => void
}

interface HookReturnType {
   triggerAPI: (arg: TriggerAPIArgType) => void
   apiStatus: APIStatus
   apiError: Error | null
}

const useFetch = (): HookReturnType => {
   const apiErrorMessagesT = useCustomT('common:apiErrorMessages')
   const [apiStatus, setAPIStatus] = useState<APIStatus>(API_INITIAL)
   const [apiError, setAPIError] = useState<Error | null>(null)

   const triggerAPI = async (arg: TriggerAPIArgType): Promise<void> => {
      try {
         setAPIStatus(API_FETCHING)

         const response = await fetch(arg.fetchUrl, {
            method: arg.method,
            headers: arg.requestHeader
         })

         if (!response.ok) {
            setAPIStatus(API_FAILED)

            const errorMessage = apiErrorMessagesT('serverError')
            const error = new Error(errorMessage)
            setAPIError(error)

            arg.onFailure(errorMessage)
            throw error
         }

         const data = await response.json()
         setAPIStatus(API_SUCCESS)
         arg.onSuccess(data)
      } catch (err) {
         console.warn('An unknown error occurred', err)

         setAPIStatus(API_FAILED)

         const error =
            err instanceof Error ? err : new Error('An unknown error occurred')
         setAPIError(error)

         arg.onFailure(error.message)
         throw error
      }
   }

   return { triggerAPI, apiStatus, apiError }
}

export default useFetch
